import { useCallback } from "react";

const RECENTLY_VISITED_KEY = "recentlyVisited";
export const RECENTLY_VISITED_PER_TYPE_MAX = 50;
const UNCAPPED_PERSIST_TYPES = new Set<RecentType>(["people", "facilities"]);

export type RecentType = "people" | "jobs" | "facilities" | "sections" | "events" | "activities" | "other";

export type RecentlyVisitedItem = {
  url: string;
  text: string;
  subtext: string;
  image: string;
  dateTime: Date;
  type?: RecentType;
};

export type GetRecentlyVisitedOptions = {
  /** Per-type display cap. `null` returns every stored item (for ranking / GraphQL ids). */
  capPerType?: number | null;
};

export function inferTypeByUrl(url: string): RecentType {
  if (url.startsWith("/people/")) return "people";
  if (url.startsWith("/jobs/")) return "jobs";
  if (url.startsWith("/facilities/")) return "facilities";
  if (url.startsWith("/sections/")) return "sections";
  if (url.startsWith("/events/") || url.startsWith("/activities/events/")) return "events";
  if (url.startsWith("/activities/")) return "activities";
  return "other";
}

export function matchesRecentFilter(itemType: RecentType, filter: "all" | RecentType): boolean {
  if (filter === "all") return true;
  if (filter === "activities") return itemType === "activities" || itemType === "events";
  return itemType === filter;
}

const JOB_POST_ID_PATTERN = /\/jobs\/job-post\/([^/]+)/;
const ACTIVITY_INSTANCE_ID_PATTERN = /\/activities\/activity-types\/[^/]+\/activity\/([^/]+)/;
const ACTIVITIES_EVENT_SLUG_PATTERN = /\/activities\/events\/([^/]+)/;
const LEGACY_EVENT_SLUG_PATTERN = /^\/events\/([^/]+)/;
const SKIPPED_ENTITY_IDS = new Set(["create", "new", "pat-results"]);

export type RecentlyViewedEntityType = "people" | "facilities" | "jobs" | "activities";

function entityIdFromRecent(item: RecentlyVisitedItem, type: RecentlyViewedEntityType): string | undefined {
  const itemType = (item.type as RecentType | undefined) || inferTypeByUrl(item.url);

  if (type === "people" || type === "facilities") {
    const prefix = type === "people" ? "/people/" : "/facilities/";
    if (itemType !== type || !item.url.startsWith(prefix)) return undefined;
    return item.url.slice(prefix.length).split("/")[0];
  }

  if (type === "jobs") {
    if (itemType !== "jobs") return undefined;
    return item.url.match(JOB_POST_ID_PATTERN)?.[1];
  }

  if (itemType !== "activities" && itemType !== "events") return undefined;
  return (
    item.url.match(ACTIVITY_INSTANCE_ID_PATTERN)?.[1] ??
    item.url.match(ACTIVITIES_EVENT_SLUG_PATTERN)?.[1] ??
    item.url.match(LEGACY_EVENT_SLUG_PATTERN)?.[1]
  );
}

export function recentlyViewedEntityIds(
  items: readonly RecentlyVisitedItem[],
  type: RecentlyViewedEntityType
): string[] {
  const ids: string[] = [];
  const seen = new Set<string>();

  for (const item of items) {
    const id = entityIdFromRecent(item, type);
    if (!id || SKIPPED_ENTITY_IDS.has(id) || seen.has(id)) continue;
    seen.add(id);
    ids.push(id);
  }

  return ids;
}

function emptyGroups(): Record<RecentType, RecentlyVisitedItem[]> {
  return {
    people: [],
    jobs: [],
    facilities: [],
    sections: [],
    events: [],
    activities: [],
    other: []
  };
}

function sortDesc(a: RecentlyVisitedItem, b: RecentlyVisitedItem) {
  return new Date(b.dateTime as unknown as string).getTime() - new Date(a.dateTime as unknown as string).getTime();
}

function takeGroup(items: RecentlyVisitedItem[], cap: number | null): RecentlyVisitedItem[] {
  return cap == null ? items : items.slice(0, cap);
}

function recentsTypeBucket(item: RecentlyVisitedItem): RecentType {
  return (item.type as RecentType | undefined) || inferTypeByUrl(item.url);
}

function isQuotaExceededError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  if (error.name === "QuotaExceededError" || error.name === "NS_ERROR_DOM_QUOTA_REACHED") return true;
  const code = "code" in error ? error.code : undefined;
  return code === 22 || code === 1014;
}

function oldestUncappedPersistIndex(items: RecentlyVisitedItem[]): number {
  let oldestIndex = -1;
  let oldestTime = Number.POSITIVE_INFINITY;

  items.forEach((item, index) => {
    if (!UNCAPPED_PERSIST_TYPES.has(recentsTypeBucket(item))) return;
    const time = new Date(item.dateTime as unknown as string).getTime();
    if (time < oldestTime) {
      oldestTime = time;
      oldestIndex = index;
    }
  });

  return oldestIndex;
}

function persistRecentlyVisited(items: RecentlyVisitedItem[]): void {
  let payload = items;

  for (;;) {
    try {
      localStorage.setItem(RECENTLY_VISITED_KEY, JSON.stringify(payload));
      return;
    } catch (error) {
      if (!isQuotaExceededError(error)) throw error;
      const oldestIndex = oldestUncappedPersistIndex(payload);
      if (oldestIndex < 0) return;
      payload = payload.filter((_, index) => index !== oldestIndex);
    }
  }
}

function normalizeByType(
  items: RecentlyVisitedItem[],
  capForType: (type: RecentType) => number | null
): RecentlyVisitedItem[] {
  const groups = emptyGroups();

  for (const item of items) {
    const type = (item.type as RecentType) || "other";
    groups[type].push({ ...item, type });
  }

  (Object.keys(groups) as RecentType[]).forEach((type) => {
    groups[type].sort(sortDesc);
  });

  const merged = [
    ...takeGroup(groups.people, capForType("people")),
    ...takeGroup(groups.jobs, capForType("jobs")),
    ...takeGroup(groups.facilities, capForType("facilities")),
    ...takeGroup(groups.sections, capForType("sections")),
    ...takeGroup(groups.events, capForType("events")),
    ...takeGroup(groups.activities, capForType("activities")),
    ...takeGroup(groups.other, capForType("other"))
  ];

  return merged.sort(sortDesc);
}

function readRecentlyVisitedFromStorage(): RecentlyVisitedItem[] {
  const raw = localStorage.getItem(RECENTLY_VISITED_KEY);
  if (!raw) {
    return [];
  }

  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as RecentlyVisitedItem[]) : [];
  } catch {
    return [];
  }
}

export function useRecentlyVisited() {
  const getRecentlyVisitedItems = useCallback((options?: GetRecentlyVisitedOptions): RecentlyVisitedItem[] => {
    const capPerType = options && "capPerType" in options ? options.capPerType : RECENTLY_VISITED_PER_TYPE_MAX;
    return normalizeByType(readRecentlyVisitedFromStorage(), () => capPerType ?? null);
  }, []);

  const addRecentlyVisitedItem = useCallback((item: Omit<RecentlyVisitedItem, "dateTime">) => {
    const existing = readRecentlyVisitedFromStorage();
    const computedType = item.type ?? inferTypeByUrl(item.url);
    const newItem: RecentlyVisitedItem = { ...item, type: computedType, dateTime: new Date() as unknown as Date };

    const deDuped = [newItem, ...existing.filter((i) => i.url !== item.url)];
    const normalized = normalizeByType(deDuped, (type) =>
      UNCAPPED_PERSIST_TYPES.has(type) ? null : RECENTLY_VISITED_PER_TYPE_MAX
    );

    persistRecentlyVisited(normalized);
  }, []);

  const removeRecentlyVisitedItem = useCallback((url: string) => {
    const existing = readRecentlyVisitedFromStorage();
    const filtered = existing.filter((item) => item.url !== url);
    persistRecentlyVisited(filtered);
  }, []);

  return { getRecentlyVisitedItems, addRecentlyVisitedItem, removeRecentlyVisitedItem };
}
